import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { getKeycloak } from './keycloak';

// Use relative URL in production, or the env variable in development
const baseURL = (import.meta as any).env?.VITE_API_URL || '/api';

const api: AxiosInstance = axios.create({
  baseURL,
});

// Add token to requests
api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  // First try to get token from window (set by keycloak.ts after backend exchange)
  const windowToken = (window as any).__keycloakToken__;
  if (windowToken) {
    config.headers.Authorization = `Bearer ${windowToken}`;
  } else {
    // Fallback to Keycloak instance
    try {
      const keycloak = getKeycloak();
      if (keycloak?.token) {
        config.headers.Authorization = `Bearer ${keycloak.token}`;
      }
    } catch {
      // Keycloak not initialized
    }
  }

  if (!(config.data instanceof FormData)) {
    config.headers['Content-Type'] = 'application/json';
  }

  return config;
});

export interface MigrationRule {
  id: string;
  type: string;
  description: string;
  matchByRoleName?: string | string[];
  matchByRoleUuid?: string | string[];
  matchBySchemaPattern?: {
    requiredProperty: string;
    propertyType: string;
  };
  priority?: number;
  enabled?: boolean;
  config: any;
}

export interface MigrationTestResult {
  transformed: any;
  wasTransformed: boolean;
  appliedRules: string[];
  errors: string[];
}

export interface DetectMismatchResult {
  hasMismatch: boolean;
  suggestions: string[];
}

export const schemaMigrationApi = {
  /**
   * Get all migration rules
   */
  async getRules(): Promise<MigrationRule[]> {
    const response = await api.get('/schema-migration/rules');
    return response.data;
  },

  /**
   * Add a new migration rule
   */
  async addRule(rule: MigrationRule): Promise<{ success: boolean; message: string }> {
    const response = await api.post('/schema-migration/rules', rule);
    return response.data;
  },

  /**
   * Update a migration rule
   */
  async updateRule(ruleId: string, updates: Partial<MigrationRule>): Promise<{ success: boolean; message: string }> {
    const response = await api.put(`/schema-migration/rules/${ruleId}`, updates);
    return response.data;
  },

  /**
   * Delete a migration rule
   */
  async deleteRule(ruleId: string): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/schema-migration/rules/${ruleId}`);
    return response.data;
  },

  /**
   * Test migration on sample data
   */
  async testMigration(
    roleName: string,
    roleVariables: any,
    roleUuid?: string,
    targetSchema?: any
  ): Promise<MigrationTestResult> {
    const response = await api.post('/schema-migration/test', {
      roleName,
      roleUuid,
      roleVariables,
      targetSchema,
    });
    return response.data;
  },

  /**
   * Detect schema mismatches and get suggestions
   */
  async detectMismatch(roleVariables: any, targetSchema: any): Promise<DetectMismatchResult> {
    const response = await api.post('/schema-migration/detect', {
      roleVariables,
      targetSchema,
    });
    return response.data;
  },
};
